<template>
  <div>
    <div class="page-head">
      <h1 class="page-title" style="border-bottom: 2px solid #eaeaea;">Job & Pay</h1>
    </div>

    <div>
      <div class="data-fetch-error" v-show="dataFetchError">
        <p>Unable to fetch data. Please try again.</p>
      </div>
    </div>

    <div class="page-loader" v-show="pageLoading">
      <div>
        <PageLoader />
      </div>
    </div>

    <div v-show="!pageLoading">
      <div class="row page-body">
        <div class="col-md-6">
          <div class="multiple-entry-form">
            <h5>Employment Details</h5>
            <div>
              <div class="d-flex info-block">
                <div class="key">Staff ID:</div>
                <div class="value">{{employee.staff_number}}</div>
              </div>

              <div class="d-flex info-block">
                <div class="key">Start Date:</div>
                <div class="value">{{employee.resumption_date}}</div>
              </div>

              <div class="d-flex info-block">
                <div class="key">Job title:</div>
                <div class="value">{{designation.name }}</div>
              </div>

              <div class="d-flex info-block">
                <div class="key">Gross Salary:</div>
                <div class="value">
                  &#8358; {{employee.gross_pay | valueToCurrency}}
                  <br />
                  <a
                    @click.prevent="showSalaryBreakdown = true"
                    class="salary-breakdown-button"
                    v-if="!showSalaryBreakdown"
                  >
                    View salary breakdown
                    <font-awesome-icon icon="angle-down" />
                  </a>
                  <a
                    @click.prevent="showSalaryBreakdown = false"
                    class="salary-breakdown-button"
                    v-if="showSalaryBreakdown"
                  >
                    View less
                    <font-awesome-icon icon="angle-up" />
                  </a>
                </div>
              </div>

              <div class="salary-breakdown" v-if="showSalaryBreakdown">
                <div class="mb-3">Compensation</div>
                <div
                  class="d-flex info-block"
                  v-for="comp in component.compensation"
                  :key="comp.index"
                >
                  <div class="key">{{comp.name}}</div>
                  <div class="value">&#8358; {{comp.value | valueToCurrency}}</div>
                </div>
              </div>

              <div class="salary-breakdown" v-if="showSalaryBreakdown">
                <div class="mb-3">Deductibles</div>
                <div class="d-flex info-block" v-for="ded in component.deductible" :key="ded.index">
                  <div class="key">{{ded.name}}</div>
                  <div class="value">&#8358; {{ded.value | valueToCurrency}}</div>
                </div>
              </div>

              <div class="d-flex info-block">
                <div class="key">Net Salary:</div>
                <div class="value">&#8358; {{employee.net_pay | valueToCurrency}}</div>
              </div>
            </div>
          </div>
        </div>
        <!-- column end -->

        <div class="col-md-6">
          <div class="multiple-entry-form bank-info">
            <div class="d-flex justify-content-between">
              <h5>Bank Information</h5>

              <div class="text-right">
                <a @click.prevent="edit()" class="edit" v-if="!editing">
                  <font-awesome-icon icon="edit" />edit
                </a>
                <a @click.prevent="closeEdit()" class="edit" v-else>
                  <font-awesome-icon icon="times" />close edit
                </a>
              </div>
            </div>

            <div>
              <div class="d-flex info-block section-error" v-if="bankInfoError">{{bankInfoError}}</div>

              <div class="d-flex info-block">
                <div class="key">Bank:</div>
                <div class="value" v-if="!editing">{{ employee.bank_account_number ? selectedBankName ? selectedBankName : '⚠️ update'  : 'loading...' }}</div>

                <div class="value" v-else>
                  <div class="custom-select__input">
                    <select
                      class="form-control form-control-sm"
                      v-model.trim="employee.bank_account_code"
                      @change="bankSelected()"
                    >
                      <option
                        v-for="(bank, index) in banks"
                        :value="bank.code"
                        :key="index"
                      >{{bank.name}}</option>
                    </select>
                    <div>
                      <font-awesome-icon icon="angle-down" />
                    </div>
                  </div>
                </div>
              </div>

              <div class="d-flex info-block">
                <div class="key">Bank Account:</div>
                <div class="value" v-if="!editing">{{employee.bank_account_number}}</div>

                <div class="value" v-else>
                  <input
                    class="form-control form-control-sm"
                    v-model.trim="employee.bank_account_number"
                    @input="getBankAccountName"
                  />
                </div>
              </div>

              <div class="d-flex info-block" v-if="!gettingBankName">
                <div class="key">Account Name:</div>
                <div class="value">{{employee.bank_account_name}}</div>
              </div>

              <div class="d-flex info-block" v-if="gettingBankName">
                <BlueLoader />
              </div>

              <div>
                <div class="error">
                  <p v-if="dataSaveError">{{dataSaveError}}</p>
                  <div v-if="serverErrors">
                    <p v-for="(value, name, index) in serverErrors" :key="index">{{value[0]}}</p>
                  </div>
                </div>
              </div>

              <div class="success" v-if="editing">
                <p v-if="dataSaveSuccess">{{dataSaveSuccess}}</p>
              </div>

              <div v-if="editing">
                <div class="submit-container">
                  <div class="button d-flex align-items-center">
                    <a @click.prevent="updateBankInformation" v-show="!loading">Save & Submit</a>
                    <WhiteLoader v-show="loading" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- column end -->
      </div>
      <!-- row1 end -->

      <div class="row page-body">
        <div class="col-md-6">
          <div class="multiple-entry-form pension-details">
            <div class="d-flex justify-content-between">
              <h5>Pension Details</h5>

              <div class="text-right">
                <a @click.prevent="editPension()" class="edit" v-if="!editingPension">
                  <font-awesome-icon icon="edit" />edit
                </a>
                <a @click.prevent="closeEditPension()" class="edit" v-else>
                  <font-awesome-icon icon="times" />close edit
                </a>
              </div>
            </div>

            <div class="d-flex info-block">
              <div class="key">PFA:</div>
              <div class="value" v-if="!editingPension">{{employee.pfa_id}}</div>
              <div class="value" v-else>
                <input class="form-control form-control-sm" v-model.trim="employee.pfa_id" />
              </div>
            </div>

            <div class="d-flex info-block">
              <div class="key">PFA Number</div>
              <div class="value" v-if="!editingPension">{{employee.pfa_number}}</div>
              <div class="value" v-else>
                <input class="form-control form-control-sm" v-model.trim="employee.pfa_number" />
              </div>
            </div>

            <div>
              <div class="error">
                <p v-if="dataSaveError">{{dataSaveError}}</p>
                <div v-if="serverErrors">
                  <p v-for="(value, name, index) in serverErrors" :key="index">{{value[0]}}</p>
                </div>
              </div>
            </div>

            <div class="success" v-if="editingPension">
              <p v-if="dataSaveSuccess">{{dataSaveSuccess}}</p>
            </div>

            <div v-if="editingPension">
              <div class="submit-container">
                <div class="button d-flex align-items-center">
                  <a @click.prevent="updatePension()" v-show="!loading">Save & Submit</a>
                  <WhiteLoader v-show="loading" />
                </div>
              </div>
            </div>

          </div>
        </div>

        <div class="col-md-6"></div>
      </div>
    </div>
  </div>
</template>

<script>
import * as types from "../../../store/constant";
import WhiteLoader from "../../partials/white-loader";
import PageLoader from "../../partials/page-loader";
import BlueLoader from "../../partials/blue-loader";
import { valueToCurrency, currencyToNumber } from "../../../util/filters";

export default {
  name: "JobPay",

  components: {
    WhiteLoader,
    PageLoader,
    BlueLoader
  },

  data() {
    return {
      item: "job-pay",
      employee: {},
      bank: {},
      designation: {},
      editing: false,
      editingPension: false,
      banks: [],
      showSalaryBreakdown: false,
      loading: false,
      pageLoading: true,

      selectedBank: {},
      selectedBankName: null,

      dataFetchError: false,
      serverErrors: {},
      dataSaveError: false,
      dataSaveSuccess: "",

      bankInfoError: null,
      gettingBankName: false,
      component: {
        compensation: [],
        deductible: []
      }
    };
  },

  created() {
    this.getEmployee();
    this.getBanks();
  },

  mounted() {
    document.getElementById("offCanvas").checked = false;
  },

  methods: {
    getEmployee() {
      this.$http
        .get(types.GET_EMPLOYEE_API)
        .then(resp => {
          this.employee = resp.data.data;

          this.selectedBankName = resp.data.data.bank_account_bank

          this.designation = resp.data.data.designations
            ? resp.data.data.designations
            : {};
          this.bank = resp.data.data.banks ? resp.data.data.banks : {};
          const comp = this.employee.components;

          for (let index of comp) {
            if (index.type === "Compensation") {
              this.component.compensation.push(index);
            }

            if (index.type === "Deductible") {
              this.component.deductible.push(index);
            }
          }
          this.pageLoading = false;
        })
        .catch(err => {
          this.pageLoading = false;
          if (err.response) {
            this.dataFetchError = true;
          }
        });
    },

    bankSelected() {
      this.employee.bank_account_number = ""
      this.employee.bank_account_name = ""

        const bank = this.banks.filter(bank => {
        if (bank.code === this.employee.bank_account_code) {
          return true;
        }
      });

      this.selectedBank = bank[0] ? bank[0] : {};
    },

    getSelectedBank() {
      const bank = this.banks.filter(bank => {
        if (bank.code === this.employee.bank_account_code) {
          return true;
        }
      });

      return bank[0].name ? bank[0].name : '--';

    },

    edit() {
      this.editing = true;
    },
    closeEdit() {
      this.serverErrors = null;
      this.dataSaveError = "";
      this.dataSaveSuccess = "";
      this.editing = false;
    },

    editPension() {
      this.editingPension = true;
    },
    closeEditPension() {
      this.serverErrors = null;
      this.dataSaveError = "";
      this.dataSaveSuccess = "";
      this.editingPension = false;
    },

    isJson(str) {
      try {
        JSON.parse(str);
      } catch (e) {
        return false;
      }
      return true;
    },

    getBanks() {
      this.$http
        .get(types.BANKS_API)
        .then(resp => {
          this.banks = resp.data.data;

          this.selectedBankName = this.getSelectedBank();
        })
        .catch(err => {
          if (err.response) {
            this.dataFetchError = true;
          }
        });
    },

    getBankAccountName() {
      this.bankInfoError = null;
      this.employee.bank_account_name = "";

      if (!this.employee.bank_account_number.match(/^[0-9]+$/)) {
        this.bankInfoError = "The account number must contain only numbers";
        return;
      }

      if (
        !this.selectedBank.code ||
        this.employee.bank_account_number.length !== 10
      ) {
        return;
      }

      let data = {
        account_number: this.employee.bank_account_number,
        bank_code: this.selectedBank.code
      };

      this.gettingBankName = true;

      this.$http
        .post(types.ACCOUNTS_API, data)
        .then(resp => {
          this.gettingBankName = false;

          if (this.isJson(resp.data.data)) {
            const data = JSON.parse(resp.data.data);

            this.employee.bank_account_name = data.account_name;
          } else {
            if (typeof resp.data.data === "string") {
              this.bankInfoError = resp.data.data;
              return;
            }

            this.employee.bank_account_name = resp.data.data.account_name;
          }
        })
        .catch(err => {
          this.gettingBankName = false;
          if (err.response) {
            const message = err.response.data.message;
            if (typeof message === "object") {
              this.bankInfoError = message;
            } else {
              this.dataSaveError = message;
            }
          }
        });
    },

    updateData() {
      return {
        firstname: this.employee.user.firstname,
        lastname: this.employee.user.lastname,
        middlename: this.employee.user.middlename,
        staff_number: this.employee.staff_number,
        resumption_date: this.employee.resumption_date,
        company_id: this.employee.company_id,
        department_id: this.employee.department_id,
        designation_id: this.employee.designation_id,
        shift_id: this.employee.shift_id,
        phone: this.employee.user.phone,
        alternate_phone: this.employee.alternate_phone_number,
        date_of_birth: this.employee.user.date_of_birth,
        gender: this.employee.user.gender,
        address: this.employee.user.address,
        country: this.employee.company_id,
        state: this.employee.state_id,
        city: this.employee.city_id,
        pre_existing_condition: this.employee.pre_existing_condition,
        is_active: this.employee.user.is_active,
        account_name: this.employee.bank_account_name,
        tax: this.$options.filters.currencyToNumber(this.employee.tax),
        basic: this.$options.filters.currencyToNumber(this.employee.basic),
        pension: this.$options.filters.currencyToNumber(this.employee.pension),
        housing: this.$options.filters.currencyToNumber(this.employee.housing),
        transport: this.$options.filters.currencyToNumber(
          this.employee.transport
        ),
        reimbursements: this.$options.filters.currencyToNumber(
          this.employee.reimbursements
        ),
        remittances: {
          pfa_number: this.employee.pfa_number,
          account_number: this.employee.bank_account_number,
          account_name: this.employee.bank_account_name,
          account_bank_code: this.selectedBank.code,
          bank_id: this.employee.bank_id,
          pfa_id: this.employee.pfa_id,
          pfa_number: this.employee.pfa_number,
          pfa: this.employee.pfa
        },
        kin_info: {
          next_of_kin: this.employee.next_of_kin,
          kin_relationship: this.employee.kin_relationship,
          kin_phone: this.employee.kin_phone
        },
        remuneration: {
          net_pay: this.$options.filters.currencyToNumber(this.netPay),
          gross_pay: this.$options.filters.currencyToNumber(this.grossPay)
        }
      };
    },

    updateBankInformation() {
      this.serverErrors = null;
      this.dataSaveError = "";
      this.dataSaveSuccess = "";

      if (!this.employee.bank_account_code) {
        this.dataSaveError = "The bank input is required.";
        return;
      }

      if (!this.employee.bank_account_number) {
        this.dataSaveError = "The bank account number is required.";
        return;
      }

      if (!this.employee.bank_account_number.match(/^[0-9]+$/)) {
        this.dataSaveError = "The bank account should be numbers only";
        return;
      }

      this.selectedBankName = this.selectedBank.name;

      this.loading = true;
      this.$http
        .put(types.UPDATE_EMPLOYEE_PAYMENT_INFORMATION, {
          account_number: this.employee.bank_account_number,
          account_name: this.employee.bank_account_name,
          account_bank_code: this.selectedBank.code,
          bank_name: this.selectedBank.name,
        })
        .then(resp => {
          this.loading = false;

          this.dataSaveSuccess = resp.data.message;
          this.closeEdit();
          this.getEmployee();
        })
        .catch(err => {
          this.loading = false;
          if (err.response) {
            const message = err.response.data.message;
            if (typeof message === "object") {
              this.serverErrors = message;
            } else {
              this.dataSaveError = message;
            }
          }
        });
    },
    updatePension() {
      this.serverErrors = null;
      this.dataSaveError = "";
      this.dataSaveSuccess = "";

      if (!this.employee.pfa) {
        this.dataSaveError = "The pfa input is required.";
        return;
      }

      if (!this.employee.pfa_number) {
        this.dataSaveError = "The pfa number is required.";
        return;
      }

      if (!this.employee.pfa_number.match(/^[0-9]+$/)) {
        this.dataSaveError = "The pfa number should be numbers only";
        return;
      }

      let updateData = this.updateData();

      this.loading = true;
      this.$http
        .put(types.GET_EMPLOYEE_API, updateData)
        .then(resp => {
          this.loading = false;
          this.dataSaveSuccess = resp.data.message;
          this.closeEditPension();
        })
        .catch(err => {
          this.loading = false;
          if (err.response) {
            const message = err.response.data.message;
            if (typeof message === "object") {
              this.serverErrors = message;
            } else {
              this.dataSaveError = message;
            }
          }
        });
    }
  },
  filters: {
    valueToCurrency,
    currencyToNumber
  }
};
</script>

<style lang="scss" scoped>
.container {
  > div {
    padding-top: 40px;
    padding-bottom: 40px;
    padding-left: 3.125vw;
    padding-right: 3.125vw;
  }
}

.success {
  margin-top: 10px;
  color: green;
  font-size: 0.938vw;
  font-style: italic;
  p {
    margin-bottom: 0;
    text-align: center;
  }
}

.error {
  margin-top: 10px;
  margin-bottom: 10px;
  text-align: center;
  color: red;
  font-size: 0.938vw;
  font-style: italic;
  p {
    margin: 0;
    padding-bottom: 10px;
  }
  > div {
    padding-bottom: 10px;
  }
}

.data-fetch-error {
  margin-top: 10px;
  margin-bottom: 40px;
  text-align: left;
  color: red;
  font-size: 0.938vw;
  font-style: italic;
  p {
    margin: 0;
    margin-top: -60px;
    margin-left: 15px;
    padding-bottom: 10px;
  }
  p::first-letter {
    text-transform: uppercase;
  }
}

.page-loader {
  margin-top: 50px;

  > div {
    width: 55px;
    margin: auto;
  }
}

.page-head {
  > h1 {
    padding-bottom: 5px;
    margin-left: 15px;
    margin-top: 0;
  }

  margin-bottom: 70px;
}

.page-body {
  margin: auto;
  margin-bottom: 30px;

  .info-block {
    padding-bottom: 40px;
  }
  .info-block:last-child {
    padding-bottom: 0;
  }

  .section-error {
    color: red;
    text-align: center;
    font-size: 13px;
  }

  .value {
    width: 60%;
    color: #5099de;
    font-size: 14px;
    font-family: Nunito;
    line-height: 1.19;
    color: #000000;

    .pension-status {
      border-radius: 5px;
      background-color: #1ec4b4 !important;
      color: #fff;
      padding: 5px;
      text-align: center;
      margin: 0px 10px 0 10px;
      width: 100px;
      display: block;
    }

    .salary-breakdown-button {
      font-family: Nunito;
      font-size: 14px;
      line-height: 2.14;
      letter-spacing: normal;
      color: #5099de;
      cursor: pointer;
    }
  }

  .salary-breakdown {
    > .info-block {
      padding-bottom: 10px;
    }
    > .info-block:last-child {
      padding-bottom: 40px;
    }
  }
  .key {
    width: 40%;
    font-weight: 200;
    color: #000000;
    font-family: Nunito;
    font-size: 14px;
    letter-spacing: normal;
    color: #707070;
  }

  .multiple-entry-form {
    border-radius: 2px;
    padding: 3vw;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
    background-color: #ffffff;

    h5 {
      margin-bottom: 50px;
      font-family: Nunito;
      font-size: 18px;
      color: #5099de;
    }
  }

  .bank-info,
  .pension-details {
    a {
      width: auto;
      font-family: Nunito;
      font-size: 14px;
      letter-spacing: 0.48px;
      color: #f3826c;
      cursor: pointer;
    }
    a:hover {
      color: #f3826c;
    }
  }

  .custom-select__input {
    border-radius: 0;
    // height: 35px;
    border: solid 1px #c7c7c7;
    background-color: #ffffff;
    overflow: hidden;
    position: relative;
  }

  .custom-select__input > div {
    position: absolute;
    color: #777777;
    top: 0;
    right: 0;
    width: 30px;
    height: 100%;
    text-align: center;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;
    align-content: center;
  }

  .custom-select__input > select {
    background-color: transparent;
    border: 0;
    outline: 0;
    padding: 0 10px;
    text-align: left;
    -moz-appearance: none;
    -webkit-appearance: none;
    position: relative;
    z-index: 2;
    font-size: 14px;
    color: #777777;
  }

  .custom-select__input > select::-ms-expand {
    display: none;
  }

  .submit-container {
    margin-top: 35px;
  }
  .submit-container a {
    height: 20px;
    font-family: Nunito;
    font-size: 14px;
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
    letter-spacing: normal;
    text-align: center;
    color: #ffffff;
    margin: auto;
  }
  .submit-container a:hover {
    color: #ffffff;
  }
  .submit-container .button {
    width: 174px;
    height: 41px;
    margin: auto;
    border-radius: 5px;
    background-color: #5099de;
    cursor: pointer;
  }

  input,
  input:focus {
    outline: none;
  }
  .form-control {
    box-shadow: none !important;
    border-radius: 0;
    border: solid 1px #c7c7c7;
    background-color: #ffffff;
  }
}

@media (max-width: 767px) {
  .bank-info {
    margin-top: 40px;
  }

  .container {
    > div {
      padding-left: 0;
      padding-right: 0;
    }
  }
}

@media (max-width: 777px) {
  .page-body {
    .multiple-entry-form {
      h5 {
        font-size: 16px;
      }
    }

    .key,
    .value {
      font-size: 12px;
    }

    .bank-info {
      a {
        font-size: 12px;
      }
    }
  }
}

@media (max-width: 358px) {
  .page-body {
    .multiple-entry-form {
      padding-top: 20px;
      padding-bottom: 20px;

      h5 {
        font-size: 16px;
      }
    }

    .key,
    .value {
      font-size: 12px;
    }

    .bank-info {
      a {
        font-size: 12px;
      }
    }
  }
}
</style>
